import { useEffect, useState } from 'react';

import {
	isUpdateAvailable,
	VERSION_POLL_INTERVAL_MS,
	VERSION_URL,
	type VersionManifest,
} from './version-check';

/**
 * Poll `/version.json` and report when a newer build is deployed. Client only.
 *
 * Checks on mount, whenever the tab becomes visible again, and on a ~5-minute
 * interval, so a long-lived tab notices a deploy before an in-app navigation
 * imports a chunk that's already gone. Once we've seen a newer build we latch
 * `true` and stop polling — the compare never flaps and the nudge doesn't
 * re-pop on every interval. Fetch failures (offline, transient) are swallowed.
 */
export function useVersionCheck(): boolean {
	const [updateAvailable, setUpdateAvailable] = useState(false);

	useEffect(() => {
		// SSR guard + no baked id: nothing to compare against.
		if (typeof window === 'undefined' || !__BUILD_ID__) {
			return;
		}

		let cancelled = false;

		async function check(): Promise<void> {
			try {
				const response = await fetch(VERSION_URL, { cache: 'no-store' });
				if (!response.ok) {
					return;
				}
				const manifest = (await response.json()) as VersionManifest;
				if (!cancelled && isUpdateAvailable(__BUILD_ID__, manifest?.buildId)) {
					setUpdateAvailable(true);
				}
			} catch {
				// Offline or a transient miss — stay quiet and try again next tick.
			}
		}

		function onVisible(): void {
			if (document.visibilityState === 'visible') {
				void check();
			}
		}

		void check();
		const interval = window.setInterval(() => void check(), VERSION_POLL_INTERVAL_MS);
		document.addEventListener('visibilitychange', onVisible);

		return () => {
			cancelled = true;
			window.clearInterval(interval);
			document.removeEventListener('visibilitychange', onVisible);
		};
	}, []);

	return updateAvailable;
}
